import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { meshExportTrackingAtom } from '@/atoms/meshExport-progress.atom';
import { clipBoxStateAtom } from '@/atoms/viewer.atom';
import { execute } from '@/graphql/execute';
import type { LayersQuery } from '@/graphql/graphql';
import { useDialog } from '@/providers/DialogProvider';
import { startMeshExport } from '@/queries/meshExport.query';
import { getProjectJobsQuery } from '@/queries/project.query';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/numeric-field';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

type LayerData = LayersQuery['layers'][number];

const MESH_CONCURRENCY_CAP = 2;

export const meshExportSchema = z.object({
  algorithm: z.enum(['terrain-tin', 'surface-poisson']),
  outputFormat: z.enum(['PLY', 'OBJ', 'GLB', 'STL']),
  outputName: z.string().min(1),
  useClipBox: z.boolean(),
  // Shared params
  voxelSize: z.number().gt(0).lte(10),
  decimationRatio: z.number().gt(0).lte(1),
  // terrain-tin params
  tileSize: z.number().min(16).max(256),
  maxEdgeLength: z.number().gt(0).lte(100),
  stitchTolerance: z.number().gt(0).lte(1),
  // surface-poisson params
  poissonDepth: z.number().int().min(6).max(13),
  normalNeighbors: z.number().int().min(10).max(100),
  poissonScale: z.number().min(1.0).max(2.0),
  poissonLinearFit: z.boolean(),
});

type MeshExportFormData = z.infer<typeof meshExportSchema>;

interface Props {
  sourceLayer: LayerData;
  projectId: string;
}

export function RunMeshExportDialog({ sourceLayer, projectId }: Props) {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const [, setTrackers] = useAtom(meshExportTrackingAtom);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const clipBoxState = useAtomValue(clipBoxStateAtom(projectId));
  const clipBoxActive = clipBoxState.enabled && clipBoxState.center !== null;

  // Concurrency cap check: count active MESH jobs for this org
  const { data: jobsData } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
  });

  const activeMeshJobs = (jobsData?.jobs ?? []).filter(
    (j) => j.type === 'MESH' && (j.status === 'WAITING' || j.status === 'PROCESSING'),
  );
  const concurrencyCapReached = activeMeshJobs.length >= MESH_CONCURRENCY_CAP;

  const form = useForm<MeshExportFormData>({
    resolver: zodResolver(meshExportSchema),
    defaultValues: {
      algorithm: 'terrain-tin',
      outputFormat: 'PLY',
      outputName: `${sourceLayer.name} Mesh`,
      useClipBox: clipBoxActive,
      // Shared
      voxelSize: 0.05,
      decimationRatio: 0.5,
      // terrain-tin
      tileSize: 64.0,
      maxEdgeLength: 5.0,
      stitchTolerance: 0.01,
      // surface-poisson
      poissonDepth: 10,
      normalNeighbors: 30,
      poissonScale: 1.1,
      poissonLinearFit: false,
    },
  });

  const watchedAlgorithm = form.watch('algorithm');
  const watchedUseClipBox = form.watch('useClipBox');

  const mutation = useMutation({
    mutationFn: async (vars: MeshExportFormData) => {
      // Build the algorithm-specific params blob
      const params: Record<string, unknown> = {
        voxelSize: vars.voxelSize,
        decimationRatio: vars.decimationRatio,
      };

      if (vars.algorithm === 'terrain-tin') {
        params.tileSize = vars.tileSize;
        params.maxEdgeLength = vars.maxEdgeLength;
        params.stitchTolerance = vars.stitchTolerance;
      } else {
        params.poissonDepth = vars.poissonDepth;
        params.normalNeighbors = vars.normalNeighbors;
        params.poissonScale = vars.poissonScale;
        params.poissonLinearFit = vars.poissonLinearFit;
      }

      // Map form algorithm to GraphQL enum
      const algorithm = vars.algorithm === 'terrain-tin' ? 'TERRAIN_TIN' : 'SURFACE_POISSON';

      // Map form output format to GraphQL enum
      const outputFormatMap: Record<string, string> = {
        PLY: 'PLY',
        OBJ: 'OBJ',
        GLB: 'GLB',
        STL: 'STL',
      };

      // Build clip box input if enabled
      const clipBox =
        vars.useClipBox && clipBoxActive && clipBoxState.center
          ? {
              center: clipBoxState.center as number[],
              halfSize: clipBoxState.halfSize as number[],
              rotation: clipBoxState.rotation as number[],
            }
          : undefined;

      return execute(startMeshExport, {
        input: {
          sourceLayerId: sourceLayer.id,
          algorithm,
          outputFormat: outputFormatMap[vars.outputFormat],
          name: vars.outputName || undefined,
          clipBox,
          params,
        },
      });
    },
    onSuccess: (data) => {
      const batchId = data.startMeshExport;
      const toastId = toast.loading(t('meshExport.runningToast', { name: sourceLayer.name }), {
        duration: Infinity,
      });
      setTrackers((prev) => [
        ...prev,
        {
          toastId,
          batchId,
          projectId,
          sourceLayerId: sourceLayer.id,
          sourceLayerName: sourceLayer.name,
        },
      ]);
      dismissActiveDialog();
    },
    onError: (err: Error) => {
      toast.error(err.message || t('meshExport.failedToStart'));
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('meshExport.title')}</DialogTitle>
        <DialogDescription>
          {t('meshExport.sourceLabel')}: <strong>{sourceLayer.name}</strong>
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit((vars) => mutation.mutate(vars))} className='flex max-h-[80vh] flex-col'>
          <div className='flex-1 space-y-4 overflow-y-auto px-1 py-1'>
            {/* Algorithm radio group */}
            <FormField
              control={form.control}
              name='algorithm'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('meshExport.algorithm')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className='flex flex-col gap-2'>
                      {(['terrain-tin', 'surface-poisson'] as const).map((algo) => (
                        <label key={algo} className='flex cursor-pointer items-start gap-2'>
                          <RadioGroupItem value={algo} id={`mesh-algo-${algo}`} />
                          <div>
                            <div className='text-sm font-medium'>
                              {t(
                                `meshExport.algorithms.${algo === 'terrain-tin' ? 'terrainTin' : 'surfacePoisson'}.label`,
                              )}
                            </div>
                            <div className='text-xs text-slate-600 dark:text-stone-400'>
                              {t(
                                `meshExport.algorithms.${algo === 'terrain-tin' ? 'terrainTin' : 'surfacePoisson'}.description`,
                              )}
                            </div>
                          </div>
                        </label>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Output format select */}
            <FormField
              control={form.control}
              name='outputFormat'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('meshExport.outputFormat')}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value='PLY'>{t('meshExport.formats.PLY.label')}</SelectItem>
                      <SelectItem value='OBJ'>{t('meshExport.formats.OBJ.label')}</SelectItem>
                      <SelectItem value='GLB'>{t('meshExport.formats.GLB.label')}</SelectItem>
                      <SelectItem value='STL'>{t('meshExport.formats.STL.label')}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            {/* Output name */}
            <FormField
              control={form.control}
              name='outputName'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('meshExport.outputName')}</FormLabel>
                  <FormControl>
                    <Input type='text' {...field} />
                  </FormControl>
                  <FormDescription>{t('meshExport.outputNameHelp')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Clip box toggle */}
            <FormField
              control={form.control}
              name='useClipBox'
              render={({ field }) => (
                <FormItem>
                  <div className='flex items-center justify-between'>
                    <FormLabel>{t('meshExport.clipBox')}</FormLabel>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} disabled={!clipBoxActive} />
                    </FormControl>
                  </div>
                  {!clipBoxActive && (
                    <FormDescription className='text-xs text-amber-600 dark:text-amber-400'>
                      {t('meshExport.clipBoxHint')}
                    </FormDescription>
                  )}
                  {watchedUseClipBox && clipBoxActive && clipBoxState.center && (
                    <FormDescription className='text-xs'>
                      {t('meshExport.clipBoxSummary', {
                        cx: clipBoxState.center[0].toFixed(1),
                        cy: clipBoxState.center[1].toFixed(1),
                        cz: clipBoxState.center[2].toFixed(1),
                        sx: (clipBoxState.halfSize[0] * 2).toFixed(1),
                        sy: (clipBoxState.halfSize[1] * 2).toFixed(1),
                        sz: (clipBoxState.halfSize[2] * 2).toFixed(1),
                      })}
                    </FormDescription>
                  )}
                </FormItem>
              )}
            />

            {/* Advanced section */}
            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <Button variant='ghost' type='button' className='w-full justify-between'>
                  <span>{t('meshExport.advanced')}</span>
                  <ChevronDown className={`size-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className='space-y-3 pt-2'>
                {/* Shared params */}
                <div className='space-y-3 rounded-md border border-slate-200/70 p-3 dark:border-stone-700/40'>
                  <div className='grid grid-cols-2 gap-3'>
                    <FormField
                      control={form.control}
                      name='voxelSize'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('meshExport.voxelSize')}</FormLabel>
                          <FormControl>
                            <NumericField value={field.value} onChange={field.onChange} step='0.01' />
                          </FormControl>
                          <FormDescription>{t('meshExport.voxelSizeHelp')}</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='decimationRatio'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('meshExport.decimationRatio')}</FormLabel>
                          <FormControl>
                            <NumericField value={field.value} onChange={field.onChange} step='0.1' />
                          </FormControl>
                          <FormDescription>{t('meshExport.decimationRatioHelp')}</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* terrain-tin params */}
                {watchedAlgorithm === 'terrain-tin' && (
                  <div className='space-y-3 rounded-md border border-slate-200/70 p-3 dark:border-stone-700/40'>
                    <FormLabel className='text-sm font-medium'>{t('meshExport.algorithms.terrainTin.label')}</FormLabel>
                    <div className='grid grid-cols-2 gap-3'>
                      <FormField
                        control={form.control}
                        name='tileSize'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('meshExport.tileSize')}</FormLabel>
                            <FormControl>
                              <NumericField value={field.value} onChange={field.onChange} step='1' />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='maxEdgeLength'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('meshExport.maxEdgeLength')}</FormLabel>
                            <FormControl>
                              <NumericField value={field.value} onChange={field.onChange} step='0.5' />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='stitchTolerance'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('meshExport.stitchTolerance')}</FormLabel>
                            <FormControl>
                              <NumericField value={field.value} onChange={field.onChange} step='0.001' />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}

                {/* surface-poisson params */}
                {watchedAlgorithm === 'surface-poisson' && (
                  <div className='space-y-3 rounded-md border border-slate-200/70 p-3 dark:border-stone-700/40'>
                    <FormLabel className='text-sm font-medium'>
                      {t('meshExport.algorithms.surfacePoisson.label')}
                    </FormLabel>
                    <div className='grid grid-cols-2 gap-3'>
                      <FormField
                        control={form.control}
                        name='poissonDepth'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('meshExport.poissonDepth')}</FormLabel>
                            <FormControl>
                              <NumericField value={field.value} onChange={field.onChange} step='1' />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='normalNeighbors'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('meshExport.normalNeighbors')}</FormLabel>
                            <FormControl>
                              <NumericField value={field.value} onChange={field.onChange} step='1' />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='poissonScale'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('meshExport.poissonScale')}</FormLabel>
                            <FormControl>
                              <NumericField value={field.value} onChange={field.onChange} step='0.1' />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name='poissonLinearFit'
                      render={({ field }) => (
                        <FormItem>
                          <div className='flex items-center gap-2'>
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormLabel className='!mt-0'>{t('meshExport.poissonLinearFit')}</FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Concurrency cap hint */}
            {concurrencyCapReached && (
              <p className='text-xs text-amber-600 dark:text-amber-400'>{t('meshExport.concurrencyHint')}</p>
            )}
          </div>

          <DialogFooter className='mt-4 shrink-0 border-t border-slate-200/70 pt-4 dark:border-stone-700/40'>
            <Button variant='outline' type='button' onClick={() => dismissActiveDialog()} disabled={mutation.isPending}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type='submit' disabled={mutation.isPending || concurrencyCapReached}>
              {mutation.isPending ? t('meshExport.starting') : t('meshExport.run')}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
